import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2, ArrowRight, Layers, Camera, ShieldCheck, Tag } from 'lucide-react';
import { quickSearch } from '@/features/store/api/products';
import { getImageUrl } from '@/shared/utils/imageUrl';

export default function NavbarSearch({ isScrolled, isMobile = false, onCloseMobile }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState({ brands: [], categories: [], products: [] });
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Focus input whenever popup opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Click outside to close popup
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  // Global keyboard shortcut (Cmd+K / Ctrl+K & ESC)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      } else if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Debounced search query
  useEffect(() => {
    if (!query.trim()) {
      setResults({ brands: [], categories: [], products: [] });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const handler = setTimeout(async () => {
      try {
        const data = await quickSearch(query, 6);
        setResults({
          brands: data.brands || [],
          categories: data.categories || [],
          products: data.products || []
        });
      } catch (err) {
        console.error('Quick search error:', err);
        setResults({ brands: [], categories: [], products: [] });
      } finally {
        setIsLoading(false);
      }
    }, 200);

    return () => clearTimeout(handler);
  }, [query]);

  const totalResultsCount = results.brands.length + results.categories.length + results.products.length;

  const handleClose = () => {
    setIsOpen(false);
    setQuery('');
    if (onCloseMobile) onCloseMobile();
  };

  const handleBrandClick = (brand) => {
    handleClose();
    navigate(`/shop/brand/${brand.slug}`);
  };

  const handleCategoryClick = (category) => {
    handleClose();
    navigate(`/shop/all?category=${category.slug}`);
  };

  const handleProductClick = (product) => {
    handleClose();
    const categoryChain = product.category_path && product.category_path.length > 0
      ? product.category_path.join('/')
      : 'uncategorized';
    navigate(`/shop/${categoryChain}/${product.slug}/`);
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (!query.trim()) return;
    const term = query.trim();
    handleClose();
    navigate(`/shop/all?search=${encodeURIComponent(term)}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit(e);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Search Icon Trigger Button */}
      {isMobile ? (
        <button
          type="button"
          onClick={() => {
            if (onCloseMobile) onCloseMobile();
            setIsOpen(prev => !prev);
          }}
          className="w-full flex items-center justify-between p-3 rounded-xl bg-[#F4F7F9] hover:bg-zinc-200/70 text-zinc-700 font-sans text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer"
        >
          <span className="flex items-center gap-2">
            <Search className="w-4 h-4 text-[#793bfb]" />
            Search Brands, Products, Categories
          </span>
          <ArrowRight className="w-3.5 h-3.5 text-zinc-400" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setIsOpen(prev => !prev)}
          title="Search brands, products, categories (⌘K)"
          aria-label="Search"
          className={`flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-xl transition-all duration-200 focus:outline-none cursor-pointer ${
            isOpen 
              ? (isScrolled ? 'bg-[#F4F7F9] text-[#793bfb]' : 'bg-white/20 text-white')
              : (isScrolled ? 'text-zinc-700 hover:text-[#793bfb] hover:bg-[#F4F7F9]' : 'text-white/80 hover:text-white hover:bg-white/10')
          }`}
        >
          {isOpen ? <X className="w-4 h-4 md:w-4.5 md:h-4.5 text-[#793bfb]" /> : <Search className="w-4 h-4 md:w-4.5 md:h-4.5" />}
        </button>
      )}

      {/* Clean Floating Search Popup (Matching Brands Dropdown style with ZERO dark background shadow) */}
      {isOpen && (
        <div className="fixed inset-x-3 top-[64px] sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:mt-3 z-50 animate-fadeIn pointer-events-auto flex justify-center sm:block">
          <div 
            className="w-full sm:w-[560px] max-w-[calc(100vw-24px)] sm:max-w-[calc(100vw-32px)] bg-white border border-zinc-200/90 rounded-2xl sm:rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.22)] overflow-hidden flex flex-col max-h-[80vh] sm:max-h-[75vh] text-zinc-800"
          >
            {/* Top Search Input Bar */}
            <div className="relative flex items-center px-3.5 sm:px-5 py-3 sm:py-3.5 border-b border-zinc-200/80 bg-white">
              <Search className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-[#793bfb] shrink-0 mr-2.5 sm:mr-3" />
              
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search brands, products, categories..."
                className="w-full bg-transparent text-xs sm:text-sm font-sans font-medium text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
              />

              <div className="flex items-center gap-1.5 shrink-0 ml-2">
                {isLoading && (
                  <Loader2 className="w-4 h-4 animate-spin text-[#793bfb]" />
                )}
                {query && (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery('');
                      inputRef.current?.focus();
                    }}
                    className="p-1 rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 cursor-pointer"
                    aria-label="Clear search"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleClose}
                  className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-zinc-500 font-mono text-[9.5px] font-bold uppercase tracking-wider transition-colors cursor-pointer"
                >
                  ESC
                </button>
              </div>
            </div>

            {/* Popup Body: Results or Suggestions */}
            <div className="overflow-y-auto p-3 sm:p-4 space-y-4 custom-scrollbar flex-1 max-h-[480px]">
              
              {/* Default State: Quick Categories / Suggestions */}
              {!query.trim() && (
                <div className="py-5 px-3 text-center space-y-3">
                  <div className="w-10 h-10 rounded-2xl bg-[#793bfb]/10 text-[#793bfb] flex items-center justify-center mx-auto">
                    <Search className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-display font-bold text-zinc-900 text-xs uppercase tracking-wide">
                      Instant Cinema Equipment Search
                    </h4>
                    <p className="text-[11px] text-zinc-500 max-w-sm mx-auto mt-0.5">
                      Search across authorized brands, categories, cinema lenses, and accessories.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-1.5 pt-1">
                    {['Atlas Lens Co.', 'Freefly Systems', 'Steadicam', 'Lenses', 'Stabilizers', 'Monitors', 'Dollies'].map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => setQuery(tag)}
                        className="px-2.5 py-1 rounded-xl bg-zinc-100 hover:bg-[#793bfb]/10 hover:text-[#793bfb] text-zinc-700 font-sans text-[11px] font-semibold transition-colors cursor-pointer"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 1. BRANDS RESULTS */}
              {results.brands.length > 0 && (
                <div className="space-y-1">
                  <div className="px-2 py-1 flex items-center gap-1.5 text-[10.5px] font-mono font-bold uppercase tracking-wider text-[#793bfb]">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    <span>Brands ({results.brands.length})</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {results.brands.map((brand) => (
                      <button
                        key={`brand-${brand.id}`}
                        type="button"
                        onClick={() => handleBrandClick(brand)}
                        className="flex items-center justify-between p-2 rounded-xl hover:bg-[#F4F7F9] transition-all text-left group border border-transparent hover:border-zinc-200/80 cursor-pointer"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-7 h-7 rounded-lg bg-zinc-100 border border-zinc-200/80 flex items-center justify-center shrink-0 overflow-hidden">
                            {brand.logo_path ? (
                              <img
                                src={getImageUrl(brand.logo_path)}
                                alt={brand.name}
                                className="w-full h-full object-contain p-0.5"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            ) : (
                              <Tag className="w-3.5 h-3.5 text-zinc-400" />
                            )}
                          </div>
                          <div className="truncate">
                            <p className="text-xs font-bold text-zinc-900 group-hover:text-[#793bfb] transition-colors truncate">
                              {brand.name}
                            </p>
                            <p className="text-[9.5px] text-zinc-500 truncate">
                              {brand.subheading || 'Brand Catalog'}
                            </p>
                          </div>
                        </div>
                        <ArrowRight className="w-3 h-3 text-zinc-400 group-hover:text-[#793bfb] group-hover:translate-x-0.5 transition-all shrink-0 ml-2" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 2. CATEGORIES RESULTS */}
              {results.categories.length > 0 && (
                <div className="space-y-1">
                  <div className="px-2 py-1 flex items-center gap-1.5 text-[10.5px] font-mono font-bold uppercase tracking-wider text-emerald-600">
                    <Layers className="w-3.5 h-3.5" />
                    <span>Categories ({results.categories.length})</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {results.categories.map((cat) => (
                      <button
                        key={`cat-${cat.id}`}
                        type="button"
                        onClick={() => handleCategoryClick(cat)}
                        className="flex items-center justify-between p-2 rounded-xl hover:bg-emerald-50/60 transition-all text-left group border border-transparent hover:border-emerald-200/80 cursor-pointer"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-7 h-7 rounded-lg bg-emerald-50 border border-emerald-200/80 flex items-center justify-center shrink-0 text-emerald-600">
                            <Layers className="w-3.5 h-3.5" />
                          </div>
                          <div className="truncate">
                            <p className="text-xs font-bold text-zinc-900 group-hover:text-emerald-700 transition-colors truncate">
                              {cat.name}
                            </p>
                            <p className="text-[9.5px] text-zinc-400 capitalize truncate">
                              {cat.parent_slug ? `In ${cat.parent_slug.replace(/-/g, ' ')}` : 'Category'}
                            </p>
                          </div>
                        </div>
                        <span className="flex items-center gap-0.5 text-[9.5px] font-mono text-emerald-600 font-bold shrink-0 ml-2">
                          Filter <ArrowRight className="w-2.5 h-2.5 group-hover:translate-x-0.5 transition-transform" />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 3. PRODUCTS RESULTS */}
              {results.products.length > 0 && (
                <div className="space-y-1">
                  <div className="px-2 py-1 flex items-center gap-1.5 text-[10.5px] font-mono font-bold uppercase tracking-wider text-amber-600">
                    <Camera className="w-3.5 h-3.5" />
                    <span>Products ({results.products.length})</span>
                  </div>
                  <div className="grid grid-cols-1 gap-1.5">
                    {results.products.map((prod) => (
                      <button
                        key={`prod-${prod.id}`}
                        type="button"
                        onClick={() => handleProductClick(prod)}
                        className="flex items-center justify-between p-2 rounded-xl hover:bg-[#F4F7F9] transition-all text-left group border border-transparent hover:border-zinc-200/80 cursor-pointer"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-zinc-100 border border-zinc-200/80 shrink-0 overflow-hidden flex items-center justify-center">
                            {prod.image ? (
                              <img
                                src={getImageUrl(prod.image)}
                                alt={prod.name}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                            ) : (
                              <Camera className="w-4 h-4 text-zinc-400" />
                            )}
                          </div>
                          <div className="truncate">
                            <p className="text-xs font-bold text-zinc-900 group-hover:text-[#793bfb] transition-colors truncate">
                              {prod.name}
                            </p>
                            <p className="text-[10px] text-zinc-500 truncate">
                              {prod.brand ? <span className="font-semibold text-zinc-700 mr-2">{prod.brand}</span> : ''}
                              {prod.price ? `₹${parseFloat(prod.price).toLocaleString('en-IN')}` : 'Call for Quote'}
                            </p>
                          </div>
                        </div>
                        <span className="flex items-center gap-1 text-[10px] font-mono text-[#793bfb] font-bold shrink-0 ml-2">
                          View <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results Fallback */}
              {query.trim() && !isLoading && totalResultsCount === 0 && (
                <div className="py-8 px-3 text-center space-y-1.5">
                  <Search className="w-7 h-7 text-zinc-300 mx-auto" />
                  <p className="text-xs font-bold text-zinc-800">
                    No results found for "{query}"
                  </p>
                  <p className="text-[11px] text-zinc-500 max-w-xs mx-auto">
                    Try searching with broader terms or check out our full catalog.
                  </p>
                </div>
              )}

            </div>

            {/* Popup Footer: View all results action */}
            {query.trim() && (
              <div className="p-2.5 bg-zinc-50 border-t border-zinc-200/80 flex flex-col sm:flex-row items-center justify-between gap-2">
                <span className="text-[9.5px] font-mono text-zinc-500 uppercase tracking-wider font-bold">
                  {totalResultsCount} matches
                </span>
                <button
                  type="button"
                  onClick={handleSearchSubmit}
                  className="w-full sm:w-auto px-3.5 py-1.5 rounded-xl bg-[#793bfb] hover:bg-[#8d52ff] text-white text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-colors shadow-sm cursor-pointer"
                >
                  <span>Search Full Catalog for "{query}"</span>
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
}
